import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Plus, Pencil, RefreshCw, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '@/services/api';
import { useFilialSelecionada } from '@/hooks/useFilialSelecionada';

const Container = styled.div`
  padding: 24px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
`;

const Title = styled.h2`
  margin: 0 0 8px 0;
`;

const Description = styled.p`
  margin: 0 0 16px 0;
  color: #4b5563;
  font-size: 14px;
`;

const TopRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #6b7280;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const Select = styled.select`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
`;

const Button = styled.button`
  height: 36px;
  padding: 0 12px;
  border: none;
  border-radius: 6px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const DangerButton = styled(Button)`
  background: #dc2626;
`;

const Tabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const TabButton = styled.button<{ $active: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? '#6366f1' : '#d1d5db')};
  background: ${({ $active }) => ($active ? '#eef2ff' : '#fff')};
  color: ${({ $active }) => ($active ? '#3730a3' : '#111827')};
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: 600;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const TableWrap = styled.div`
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin: 14px 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th, td {
    border-bottom: 1px solid #e5e7eb;
    padding: 8px;
    text-align: left;
    white-space: nowrap;
  }
  th {
    background: #f9fafb;
    font-weight: 600;
  }
`;

const HorariosTable = styled(Table)`
  table-layout: fixed;
  font-size: 12px;
  th, td {
    padding: 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const SectionTitle = styled.h3`
  margin: 16px 0 10px 0;
  font-size: 16px;
`;

const CheckRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

type HorarioForm = {
  Nome: string;
  Entrada01: string;
  Saida01: string;
  Entrada02: string;
  Saida02: string;
  Ativo: boolean;
};

type EscalaForm = {
  Nome: string;
  CodigoHorarioDomingo: string;
  CodigoHorarioSegunda: string;
  CodigoHorarioTerca: string;
  CodigoHorarioQuarta: string;
  CodigoHorarioQuinta: string;
  CodigoHorarioSexta: string;
  CodigoHorarioSabado: string;
  Ativo: boolean;
};

type FuncionarioForm = {
  Controle: number;
  Nome: string;
  Escala: string;
};

const horarioInicial: HorarioForm = {
  Nome: '',
  Entrada01: '',
  Saida01: '',
  Entrada02: '',
  Saida02: '',
  Ativo: true
};

const escalaInicial: EscalaForm = {
  Nome: '',
  CodigoHorarioDomingo: '',
  CodigoHorarioSegunda: '',
  CodigoHorarioTerca: '',
  CodigoHorarioQuarta: '',
  CodigoHorarioQuinta: '',
  CodigoHorarioSexta: '',
  CodigoHorarioSabado: '',
  Ativo: true
};

const flagToBool = (value: any) => String(value || '').toUpperCase() !== 'N' && String(value || '1') !== '0';
const normalizeHorarioVinculo = (value: any): string => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (/^\d+$/.test(raw)) return String(Number(raw));
  return raw;
};

const PontoEscalas: React.FC = () => {
  const { filialSelecionada } = useFilialSelecionada();
  const filialId = filialSelecionada?.id || '';
  const [aba, setAba] = useState<'horarios' | 'escalas' | 'funcionarios'>('horarios');
  const [carregando, setCarregando] = useState(false);
  const [horarios, setHorarios] = useState<any[]>([]);
  const [escalas, setEscalas] = useState<any[]>([]);
  const [funcionarios, setFuncionarios] = useState<any[]>([]);
  const [formHorario, setFormHorario] = useState<HorarioForm>(horarioInicial);
  const [formEscala, setFormEscala] = useState<EscalaForm>(escalaInicial);
  const [editandoHorario, setEditandoHorario] = useState<number | null>(null);
  const [editandoEscala, setEditandoEscala] = useState<number | null>(null);
  const [edicaoFuncionario, setEdicaoFuncionario] = useState<FuncionarioForm | null>(null);

  const horariosPorCodigo = useMemo(() => {
    const map = new Map<string, string>();
    for (const h of horarios) {
      const controle = String(h?.Controle || '');
      const codigoLegado = String(h?.Codigo || '');
      const nome = String(h?.Nome || controle || codigoLegado || '');
      if (controle) map.set(controle, nome);
      if (codigoLegado) map.set(codigoLegado, nome);
    }
    return map;
  }, [horarios]);

  const escalasPorControle = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of escalas) {
      const controle = normalizeHorarioVinculo(e?.Controle);
      if (controle) map.set(controle, String(e?.Nome || controle));
    }
    return map;
  }, [escalas]);

  const carregarDados = async (targetFilialId: string) => {
    if (!targetFilialId) return;
    setCarregando(true);
    try {
      const [listaHorarios, listaEscalas, listaFuncionarios] = await Promise.all([
        apiService.listarPontoHorarios(targetFilialId, false),
        apiService.listarPontoEscalas(targetFilialId, false),
        apiService.listarPontoFuncionarios(targetFilialId, true)
      ]);
      setHorarios(listaHorarios);
      setEscalas(listaEscalas);
      setFuncionarios(listaFuncionarios);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Falha ao carregar horários, escalas e funcionários');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (!filialId) return;
    carregarDados(filialId);
  }, [filialId]);

  const onChangeHorario = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormHorario((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const onChangeEscala = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormEscala((prev) => ({ ...prev, [name]: value }));
  };

  const novoHorario = () => {
    setEditandoHorario(null);
    setFormHorario(horarioInicial);
  };

  const novaEscala = () => {
    setEditandoEscala(null);
    setFormEscala(escalaInicial);
  };

  const editarHorario = async (controle: number) => {
    if (!filialId) return;
    try {
      const item = await apiService.obterPontoHorario(filialId, controle);
      if (!item) return;
      setEditandoHorario(controle);
      setFormHorario({
        Nome: String(item.Nome || ''),
        Entrada01: String(item.Entrada01 || ''),
        Saida01: String(item.Saida01 || ''),
        Entrada02: String(item.Entrada02 || ''),
        Saida02: String(item.Saida02 || ''),
        Ativo: flagToBool(item.Ativo)
      });
      setAba('horarios');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Falha ao carregar horário');
    }
  };

  const editarEscala = async (controle: number) => {
    if (!filialId) return;
    try {
      const item = await apiService.obterPontoEscala(filialId, controle);
      if (!item) return;
      setEditandoEscala(controle);
      setFormEscala({
        Nome: String(item.Nome || ''),
        CodigoHorarioDomingo: normalizeHorarioVinculo(item.CodigoHorarioDomingo),
        CodigoHorarioSegunda: normalizeHorarioVinculo(item.CodigoHorarioSegunda),
        CodigoHorarioTerca: normalizeHorarioVinculo(item.CodigoHorarioTerca),
        CodigoHorarioQuarta: normalizeHorarioVinculo(item.CodigoHorarioQuarta),
        CodigoHorarioQuinta: normalizeHorarioVinculo(item.CodigoHorarioQuinta),
        CodigoHorarioSexta: normalizeHorarioVinculo(item.CodigoHorarioSexta),
        CodigoHorarioSabado: normalizeHorarioVinculo(item.CodigoHorarioSabado),
        Ativo: flagToBool(item.Ativo)
      });
      setAba('escalas');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Falha ao carregar escala');
    }
  };

  const salvarHorario = async () => {
    if (!filialId) {
      toast.error('Selecione a filial');
      return;
    }
    if (!formHorario.Nome.trim()) {
      toast.error('Informe o nome do horário');
      return;
    }
    const payload = {
      filialId,
      ...formHorario,
      Ativo: formHorario.Ativo ? 'S' : 'N'
    };
    try {
      if (editandoHorario) {
        await apiService.atualizarPontoHorario(filialId, editandoHorario, payload);
      } else {
        await apiService.criarPontoHorario(payload);
      }
      toast.success('Horário salvo com sucesso');
      await carregarDados(filialId);
      novoHorario();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Falha ao salvar horário');
    }
  };

  const salvarEscala = async () => {
    if (!filialId) {
      toast.error('Selecione a filial');
      return;
    }
    if (!formEscala.Nome.trim()) {
      toast.error('Informe o nome da escala');
      return;
    }
    const payload = {
      filialId,
      ...formEscala,
      Ativo: formEscala.Ativo ? 'S' : 'N'
    };
    try {
      if (editandoEscala) {
        await apiService.atualizarPontoEscala(filialId, editandoEscala, payload);
      } else {
        await apiService.criarPontoEscala(payload);
      }
      toast.success('Escala salva com sucesso');
      await carregarDados(filialId);
      novaEscala();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Falha ao salvar escala');
    }
  };

  const excluirHorario = async (controle: number, nome: string) => {
    if (!filialId) return;
    if (!confirm(`Tem certeza que deseja excluir o horário "${nome}"?`)) return;
    try {
      await apiService.excluirPontoHorario(filialId, controle);
      toast.success('Horário excluído com sucesso');
      await carregarDados(filialId);
      if (editandoHorario === controle) novoHorario();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Falha ao excluir horário');
    }
  };

  const excluirEscala = async (controle: number, nome: string) => {
    if (!filialId) return;
    if (!confirm(`Tem certeza que deseja excluir a escala "${nome}"?`)) return;
    try {
      await apiService.excluirPontoEscala(filialId, controle);
      toast.success('Escala excluída com sucesso');
      await carregarDados(filialId);
      if (editandoEscala === controle) novaEscala();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Falha ao excluir escala');
    }
  };

  const iniciarEdicaoFuncionario = (item: any) => {
    setEdicaoFuncionario({
      Controle: Number(item.Controle),
      Nome: String(item.Nome || ''),
      Escala: normalizeHorarioVinculo(item.Escala)
    });
    setAba('funcionarios');
  };

  const salvarEscalaFuncionario = async () => {
    if (!filialId || !edicaoFuncionario) return;
    try {
      await apiService.atualizarEscalaFuncionario(
        filialId,
        edicaoFuncionario.Controle,
        normalizeHorarioVinculo(edicaoFuncionario.Escala)
      );
      toast.success('Escala do funcionário atualizada com sucesso');
      await carregarDados(filialId);
      setEdicaoFuncionario(null);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Falha ao atualizar escala do funcionário');
    }
  };

  const selectHorarioOptions = (
    <>
      <option value="">-- sem vínculo --</option>
      {horarios.map((h) => (
        <option key={`hopt-${h.Controle}`} value={String(h.Controle || '')}>
          {String(h.Controle || '')} - {String(h.Nome || '')}
        </option>
      ))}
    </>
  );

  const selectEscalaOptions = (
    <>
      <option value="">-- sem vínculo --</option>
      {escalas.map((e) => (
        <option key={`eopt-${e.Controle}`} value={normalizeHorarioVinculo(e.Controle)}>
          {normalizeHorarioVinculo(e.Controle)} - {String(e.Nome || '')}
        </option>
      ))}
    </>
  );

  return (
    <Container>
      <Card>
        <Title>Cadastro de Escalas de Horário</Title>
        <Description>
          Gestão por filial dos cadastros legados <code>tpontohorario</code> e <code>tpontoescala</code>.
        </Description>

        <TopRow>
          <div style={{ fontSize: 13, color: '#4b5563' }}>
            Filial selecionada: <strong>{filialSelecionada?.nome || 'nenhuma'}</strong>
          </div>
          <Button disabled={carregando || !filialId} onClick={() => carregarDados(filialId)}>
            <RefreshCw size={16} /> Atualizar
          </Button>
        </TopRow>

        <Tabs>
          <TabButton $active={aba === 'horarios'} onClick={() => setAba('horarios')}>
            Horários
          </TabButton>
          <TabButton $active={aba === 'escalas'} onClick={() => setAba('escalas')}>
            Escalas
          </TabButton>
          <TabButton $active={aba === 'funcionarios'} onClick={() => setAba('funcionarios')}>
            Funcionários
          </TabButton>
        </Tabs>

        {aba === 'horarios' && (
          <>
            <SectionTitle>Horários cadastrados</SectionTitle>
            <TableWrap>
              <HorariosTable>
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>Controle</th>
                    <th style={{ width: 220 }}>Nome</th>
                    <th style={{ width: 95 }}>Entrada01</th>
                    <th style={{ width: 95 }}>Saida01</th>
                    <th style={{ width: 95 }}>Entrada02</th>
                    <th style={{ width: 95 }}>Saida02</th>
                    <th style={{ width: 200 }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {horarios.map((item) => (
                    <tr key={`horario-${item.Controle}`}>
                      <td>{item.Controle}</td>
                      <td>{String(item.Nome || '-')}</td>
                      <td>{String(item.Entrada01 || '-')}</td>
                      <td>{String(item.Saida01 || '-')}</td>
                      <td>{String(item.Entrada02 || '-')}</td>
                      <td>{String(item.Saida02 || '-')}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Button onClick={() => editarHorario(Number(item.Controle))}>
                            <Pencil size={14} /> Editar
                          </Button>
                          <DangerButton onClick={() => excluirHorario(Number(item.Controle), String(item.Nome || 'sem nome'))}>
                            <Trash2 size={14} /> Excluir
                          </DangerButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </HorariosTable>
            </TableWrap>

            <TopRow>
              <Button onClick={novoHorario}>
                <Plus size={16} /> Novo horário
              </Button>
            </TopRow>

            <Grid>
              <Field><Label>Nome *</Label><Input name="Nome" value={formHorario.Nome} onChange={onChangeHorario} /></Field>
              <Field><Label>Entrada01</Label><Input name="Entrada01" value={formHorario.Entrada01} onChange={onChangeHorario} placeholder="08:00" /></Field>
              <Field><Label>Saida01</Label><Input name="Saida01" value={formHorario.Saida01} onChange={onChangeHorario} placeholder="12:00" /></Field>
              <Field><Label>Entrada02</Label><Input name="Entrada02" value={formHorario.Entrada02} onChange={onChangeHorario} placeholder="13:00" /></Field>
              <Field><Label>Saida02</Label><Input name="Saida02" value={formHorario.Saida02} onChange={onChangeHorario} placeholder="17:00" /></Field>
            </Grid>
            <CheckRow>
              <input type="checkbox" checked={formHorario.Ativo} onChange={(e) => setFormHorario((p) => ({ ...p, Ativo: e.target.checked }))} />
              Ativo
            </CheckRow>
            <TopRow>
              <Button onClick={salvarHorario}>Salvar horário</Button>
            </TopRow>
          </>
        )}

        {aba === 'escalas' && (
          <>
            <SectionTitle>Escalas cadastradas</SectionTitle>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>Controle</th>
                    <th>Nome</th>
                    <th>Segunda</th>
                    <th>Sexta</th>
                    <th>Sábado</th>
                    <th>Ativo</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {escalas.map((item) => (
                    <tr key={`escala-${item.Controle}`}>
                      <td>{item.Controle}</td>
                      <td>{String(item.Nome || '-')}</td>
                      <td>
                        {String(
                          item.NomeHorarioSegunda ||
                          horariosPorCodigo.get(String(item.CodigoHorarioSegunda || '')) ||
                          horariosPorCodigo.get(normalizeHorarioVinculo(item.CodigoHorarioSegunda)) ||
                          item.CodigoHorarioSegunda ||
                          '-'
                        )}
                      </td>
                      <td>
                        {String(
                          item.NomeHorarioSexta ||
                          horariosPorCodigo.get(String(item.CodigoHorarioSexta || '')) ||
                          horariosPorCodigo.get(normalizeHorarioVinculo(item.CodigoHorarioSexta)) ||
                          item.CodigoHorarioSexta ||
                          '-'
                        )}
                      </td>
                      <td>
                        {String(
                          item.NomeHorarioSabado ||
                          horariosPorCodigo.get(String(item.CodigoHorarioSabado || '')) ||
                          horariosPorCodigo.get(normalizeHorarioVinculo(item.CodigoHorarioSabado)) ||
                          item.CodigoHorarioSabado ||
                          '-'
                        )}
                      </td>
                      <td>{flagToBool(item.Ativo) ? 'Sim' : 'Não'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Button onClick={() => editarEscala(Number(item.Controle))}>
                            <Pencil size={14} /> Editar
                          </Button>
                          <DangerButton onClick={() => excluirEscala(Number(item.Controle), String(item.Nome || 'sem nome'))}>
                            <Trash2 size={14} /> Excluir
                          </DangerButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>

            <TopRow>
              <Button onClick={novaEscala}>
                <Plus size={16} /> Nova escala
              </Button>
            </TopRow>

            <Grid>
              <Field><Label>Nome *</Label><Input name="Nome" value={formEscala.Nome} onChange={onChangeEscala} /></Field>
              <Field><Label>Domingo</Label><Select name="CodigoHorarioDomingo" value={formEscala.CodigoHorarioDomingo} onChange={onChangeEscala}>{selectHorarioOptions}</Select></Field>
              <Field><Label>Segunda</Label><Select name="CodigoHorarioSegunda" value={formEscala.CodigoHorarioSegunda} onChange={onChangeEscala}>{selectHorarioOptions}</Select></Field>
              <Field><Label>Terça</Label><Select name="CodigoHorarioTerca" value={formEscala.CodigoHorarioTerca} onChange={onChangeEscala}>{selectHorarioOptions}</Select></Field>
              <Field><Label>Quarta</Label><Select name="CodigoHorarioQuarta" value={formEscala.CodigoHorarioQuarta} onChange={onChangeEscala}>{selectHorarioOptions}</Select></Field>
              <Field><Label>Quinta</Label><Select name="CodigoHorarioQuinta" value={formEscala.CodigoHorarioQuinta} onChange={onChangeEscala}>{selectHorarioOptions}</Select></Field>
              <Field><Label>Sexta</Label><Select name="CodigoHorarioSexta" value={formEscala.CodigoHorarioSexta} onChange={onChangeEscala}>{selectHorarioOptions}</Select></Field>
              <Field><Label>Sábado</Label><Select name="CodigoHorarioSabado" value={formEscala.CodigoHorarioSabado} onChange={onChangeEscala}>{selectHorarioOptions}</Select></Field>
            </Grid>
            <CheckRow>
              <input type="checkbox" checked={formEscala.Ativo} onChange={(e) => setFormEscala((p) => ({ ...p, Ativo: e.target.checked }))} />
              Ativo
            </CheckRow>
            <TopRow>
              <Button onClick={salvarEscala}>Salvar escala</Button>
            </TopRow>
          </>
        )}

        {aba === 'funcionarios' && (
          <>
            <SectionTitle>Funcionários</SectionTitle>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>Controle</th>
                    <th>Nome</th>
                    <th>Escala</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {funcionarios.map((item) => (
                    <tr key={`func-${item.Controle}`}>
                      <td>{item.Controle}</td>
                      <td>{String(item.Nome || '-')}</td>
                      <td>
                        {String(
                          item.NomeEscala ||
                          escalasPorControle.get(normalizeHorarioVinculo(item.Escala)) ||
                          item.Escala ||
                          '-'
                        )}
                      </td>
                      <td>
                        <Button onClick={() => iniciarEdicaoFuncionario(item)}>
                          <Pencil size={14} /> Editar escala
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>

            {edicaoFuncionario && (
              <>
                <SectionTitle>Editar escala do funcionário</SectionTitle>
                <Grid>
                  <Field>
                    <Label>Funcionário</Label>
                    <Input value={`${edicaoFuncionario.Controle} - ${edicaoFuncionario.Nome}`} readOnly />
                  </Field>
                  <Field>
                    <Label>Escala</Label>
                    <Select
                      value={edicaoFuncionario.Escala}
                      onChange={(e) => setEdicaoFuncionario((prev) => (prev ? { ...prev, Escala: e.target.value } : prev))}
                    >
                      {selectEscalaOptions}
                    </Select>
                  </Field>
                </Grid>
                <TopRow>
                  <Button onClick={salvarEscalaFuncionario}>Salvar escala do funcionário</Button>
                  <DangerButton onClick={() => setEdicaoFuncionario(null)}>Cancelar</DangerButton>
                </TopRow>
              </>
            )}
          </>
        )}
      </Card>
    </Container>
  );
};

export default PontoEscalas;
